import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { providerApiClient } from '@/lib/api'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import TreatmentPlanEditor from './TreatmentPlanEditor'
import { queueKeys } from '@/lib/queryKeys'
import type { DocumentationState, DocumentationAction } from '@/types/documentation'

interface DocumentationTabProps {
  visitId: string
  state: DocumentationState
  dispatch: React.Dispatch<DocumentationAction>
  clearDraft: () => void
}

export default function DocumentationTab({
  visitId,
  state,
  dispatch,
  clearDraft,
}: DocumentationTabProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const completeVisitMutation = useMutation({
    mutationFn: () =>
      providerApiClient.completeVisit(visitId, {
        note_content: state.noteContent,
        diagnosis_codes: state.diagnosisCodes,
        procedure_codes: state.procedureCodes,
        prescriptions: state.prescriptions,
        treatment_plan: state.treatmentPlan,
        follow_up_weeks: state.followUpWeeks,
        follow_up_date: state.followUpDate,
      }),
    onSuccess: () => {
      clearDraft()
      queryClient.invalidateQueries({ queryKey: queueKeys.all })
      toast.success('Visit completed successfully')
      navigate('/visits')
    },
    onError: () => {
      toast.error('Failed to complete visit')
    },
  })

  const canComplete = state.treatmentPlanApproved

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-4">
      <div className="flex-1 overflow-y-auto">
        <ErrorBoundary fallback={<div className="rounded-lg border bg-white p-6 text-sm text-red-600">Failed to load treatment plan. Please refresh.</div>}>
          <TreatmentPlanEditor
            treatmentPlan={state.treatmentPlan}
            treatmentPlanApproved={state.treatmentPlanApproved}
            dispatch={dispatch}
            prescriptions={state.prescriptions}
            followUpWeeks={state.followUpWeeks}
            followUpDate={state.followUpDate}
          />
        </ErrorBoundary>
      </div>

      {/* Sticky complete button */}
      <div className="flex-none rounded-lg border bg-white p-4 shadow-xs">
        <Button
          onClick={() => completeVisitMutation.mutate()}
          disabled={!canComplete || completeVisitMutation.isPending}
          className="w-full"
        >
          <Send className="h-4 w-4 mr-2" />
          {completeVisitMutation.isPending
            ? 'Completing...'
            : 'Complete Visit & Send Treatment Plan'}
        </Button>
        {!canComplete && (
          <p className="text-xs text-slate-500 mt-2 text-center">
            Approve the treatment plan to complete the visit.
          </p>
        )}
      </div>
    </div>
  )
}
